import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useUserType } from "@/hooks/useUserType";
import { useCourseContext } from "@/contexts/CourseContext";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import CourseCreationChat from "@/components/CourseCreationChat";
import AddieOutlineView from "@/components/AddieOutlineView";
import { Download, FileText, Loader2 } from "lucide-react";
import { PublicNav } from "@/components/PublicNav";

export default function Create() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { session, isLoading: authLoading } = useAuth();
  const { userType, isLoading: typeLoading } = useUserType(session?.user?.id);
  const { setCourseContext } = useCourseContext();
  const [showChat, setShowChat] = useState(true);
  const [initialTopic, setInitialTopic] = useState("");
  const [project, setProject] = useState<{ id: string; title: string } | null>(null);
  const [addieOutline, setAddieOutline] = useState<any>(null);
  const [loadingOutline, setLoadingOutline] = useState(true);

  const workspaceIdFromUrl = searchParams.get("workspaceId");
  const effectiveWorkspaceIdParam = userType === "other_one_course" ? null : workspaceIdFromUrl;

  const { data: workspaceAccess, isLoading: workspaceAccessLoading } = useQuery({
    queryKey: ["create-workspace-access", session?.user?.id, effectiveWorkspaceIdParam],
    queryFn: async (): Promise<{ id: string; name: string } | null> => {
      if (!effectiveWorkspaceIdParam || !session?.user?.id) return null;
      const { data: ws } = await supabase
        .from("workspaces")
        .select("id, name, user_id")
        .eq("id", effectiveWorkspaceIdParam)
        .maybeSingle();
      if (!ws) return null;
      if (ws.user_id === session.user.id) return { id: ws.id, name: ws.name };
      const { data: mem } = await supabase
        .from("workspace_members")
        .select("role")
        .eq("workspace_id", effectiveWorkspaceIdParam)
        .eq("user_id", session.user.id)
        .maybeSingle();
      if (mem?.role === "owner" || mem?.role === "editor") return { id: ws.id, name: ws.name };
      return null;
    },
    enabled: !!session?.user?.id && !!effectiveWorkspaceIdParam,
  });

  // Redirect only learners and unauthenticated
  useEffect(() => {
    if (authLoading || typeLoading) return;
    if (!session) {
      navigate("/auth", { replace: true });
      return;
    }
    if (userType === "learner") {
      navigate("/learn", { replace: true });
    }
  }, [session, userType, authLoading, typeLoading, navigate]);

  // Fetch single project and ADDIE outline for other_one_course
  useEffect(() => {
    if (userType !== "other_one_course" || !session?.user?.id) return;
    let cancelled = false;
    (async () => {
      setLoadingOutline(true);
      const { data: projects } = await supabase
        .from("learning_projects")
        .select("id, title")
        .eq("user_id", session.user.id)
        .limit(1);
      if (cancelled || !projects?.length) {
        setLoadingOutline(false);
        return;
      }
      const proj = projects[0];
      setProject(proj);
      const { data: outline } = await supabase
        .from("addie_outlines")
        .select("*")
        .eq("project_id", proj.id)
        .maybeSingle();
      if (!cancelled) {
        setAddieOutline(outline ?? null);
        setLoadingOutline(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userType, session?.user?.id]);

  useEffect(() => {
    if (!effectiveWorkspaceIdParam || workspaceAccessLoading) return;
    if (!workspaceAccess) {
      toast({
        variant: "destructive",
        title: "Workspace unavailable",
        description: "You do not have access to this workspace, or it does not exist.",
      });
      navigate("/dashboard", { replace: true });
    }
  }, [effectiveWorkspaceIdParam, workspaceAccessLoading, workspaceAccess, navigate, toast]);

  useEffect(() => {
    if (authLoading || typeLoading) return;
    if (userType === "learner") return;
    if (workspaceAccess) {
      setCourseContext({
        courseId: null,
        courseName: null,
        nodeId: null,
        nodeName: null,
        pageType: "course-creation",
        workspaceId: workspaceAccess.id,
        workspaceName: workspaceAccess.name,
      });
      return;
    }
    if (effectiveWorkspaceIdParam && workspaceAccessLoading) return;
    if (userType === "other_no_course" || userType === "admin" || userType === "designer") {
      setCourseContext({
        courseId: null,
        courseName: null,
        nodeId: null,
        nodeName: null,
        pageType: "course-creation",
        workspaceId: null,
        workspaceName: null,
      });
    }
  }, [
    authLoading,
    typeLoading,
    userType,
    workspaceAccess,
    effectiveWorkspaceIdParam,
    workspaceAccessLoading,
    setCourseContext,
  ]);

  // Listen for topic from Course Assistant (e.g. "open creation flow for X")
  useEffect(() => {
    const handler = (e: CustomEvent<{ topic: string }>) => {
      const topic = e.detail?.topic?.trim();
      if (topic) setInitialTopic(topic);
    };
    window.addEventListener("open-creation-chat", handler as EventListener);
    return () => window.removeEventListener("open-creation-chat", handler as EventListener);
  }, []);

  const handleCreateSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["user-type"] });
    queryClient.invalidateQueries({ queryKey: ["is-designer"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard-workspaces"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard-unassigned-courses"] });
    setShowChat(false);
  };

  const downloadOutline = () => {
    if (!project) return;
    const parts: string[] = [`# ${project.title}\n`];
    if (addieOutline) {
      const o = addieOutline;
      if (o.analysis && typeof o.analysis === "object") {
        parts.push("## Analysis\n", JSON.stringify(o.analysis, null, 2), "\n");
      }
      if (o.design && typeof o.design === "object") {
        parts.push("## Design\n", JSON.stringify(o.design, null, 2), "\n");
      }
      if (o.development && typeof o.development === "object") {
        parts.push("## Development\n", JSON.stringify(o.development, null, 2), "\n");
      }
      if (o.implementation && typeof o.implementation === "object") {
        parts.push("## Implementation\n", JSON.stringify(o.implementation, null, 2), "\n");
      }
      if (o.evaluation && typeof o.evaluation === "object") {
        parts.push("## Evaluation\n", JSON.stringify(o.evaluation, null, 2), "\n");
      }
    } else {
      parts.push("No ADDIE outline generated yet. Open your course to generate one.");
    }
    const blob = new Blob([parts.join("")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(project.title || "course").replace(/[^a-zA-Z0-9-_]/g, "_")}-outline.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (authLoading || typeLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }
  if (!session) return null;

  // Learner should not land here (they go to /learn)
  if (userType === "learner") {
    navigate("/learn", { replace: true });
    return null;
  }

  if (effectiveWorkspaceIdParam && workspaceAccessLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading workspace…</p>
      </div>
    );
  }

  if (effectiveWorkspaceIdParam && !workspaceAccessLoading && !workspaceAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Redirecting…</p>
      </div>
    );
  }

  const showCreationChat =
    userType === "other_no_course" ||
    userType === "admin" ||
    userType === "designer" ||
    !!workspaceAccess;

  if (showCreationChat) {
    return (
      <div className="public-page min-h-screen bg-background flex flex-col">
        <PublicNav />
        {workspaceAccess ? (
          <p className="text-center text-sm text-muted-foreground border-b bg-muted/40 py-2.5 px-4">
            This course will be created in{" "}
            <span className="font-medium text-foreground">{workspaceAccess.name}</span>
          </p>
        ) : null}
        <div className="flex-1 flex items-stretch justify-center min-h-0">
          <CourseCreationChat
            open={showChat}
            onOpenChange={() => {}}
            onSuccess={handleCreateSuccess}
            workspaceId={workspaceAccess?.id}
            initialTopic={initialTopic || undefined}
          />
        </div>
      </div>
    );
  }

  // other_one_course: show outline + download only
  if (userType === "other_one_course") {
    return (
      <div className="public-page min-h-screen bg-background flex flex-col">
        <PublicNav />
        <div className="flex-1 p-4 md:p-8">
          <div className="max-w-3xl mx-auto space-y-6">
            <Card className="border-border bg-card/95">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Your course
                </CardTitle>
              <CardDescription>
                You can view and download your course outline. Create an account to build more courses and add lessons.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingOutline ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading…
                </div>
              ) : project ? (
                <>
                  <p className="font-medium text-foreground">{project.title}</p>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={downloadOutline} className="gap-2">
                      <Download className="h-4 w-4" />
                      Download course outline
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/project/${project.id}`)}
                      className="gap-2"
                    >
                      View course
                    </Button>
                  </div>
                  {addieOutline ? (
                    <div className="pt-4 border-t mt-4">
                      <AddieOutlineView outline={addieOutline} />
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground pt-2">
                      No ADDIE outline yet. Open your course to generate one.
                    </p>
                  )}
                </>
              ) : (
                <p className="text-muted-foreground">No course found.                </p>
              )}
            </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
